<template>
    <BaseField
        :label="label"
        :placeholder="placeholder"
        :hint="hint"
        :is-optional="isOptional"
        :name="name"
        :error-messages="errorMessagesTotal"
        data-spec-class="field-time"
        :custom-theme-base="FormTheme.CustomerArea"
    >
        <div class="flex flex-col">
            <div class="flex items-center">
                <button
                    type="button"
                    :disabled="isDisabled"
                    class="flex h-10 w-10 items-center justify-center rounded-l border-none text-xl text-brand hover:text-brand--light focus:text-brand--light focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
                    @click="decrementTime"
                >
                    −
                </button>

                <div
                    :class="[
                        'flex h-10 w-16 border-b-2',
                        {
                            'border-danger': errorMessagesTotal.length && !isDisabled,
                            'border-brand': !errorMessagesTotal.length && !isDisabled,
                            'border-secondary--inverse': isDisabled,
                        },
                    ]"
                >
                    <input
                        ref="hourInput"
                        type="text"
                        :value="hourValue"
                        :disabled="isDisabled"
                        class="block w-6 rounded-none border-0 border-b border-none border-divider bg-transparent px-0 text-right shadow-none focus:ring-0 focus:outline-hidden sm:text-sm"
                        :class="inputClassObject"
                        data-spec-class="field-time__hour"
                        @input="handleHourInput"
                        @blur="handleHourBlur"
                    />

                    <div class="flex w-2 items-center justify-center text-sm text-tertiary">:</div>

                    <input
                        ref="minuteInput"
                        type="text"
                        :value="minuteDisplayValue"
                        :disabled="isDisabled"
                        class="block w-6 rounded-none border-0 border-b border-none border-divider bg-transparent px-0 text-left shadow-none focus:ring-0 focus:outline-hidden sm:text-sm"
                        :class="inputClassObject"
                        data-spec-class="field-time__minute"
                        @input="handleMinuteInput"
                        @blur="handleMinuteBlur"
                    />
                </div>

                <button
                    type="button"
                    :disabled="isDisabled"
                    class="flex h-10 w-10 items-center justify-center rounded-l border-none text-xl text-brand hover:text-brand--light focus:text-brand--light focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
                    @click="incrementTime"
                >
                    +
                </button>
            </div>
        </div>
    </BaseField>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { FormTheme } from '@/types';
import BaseField from './BaseField.vue';

const emit = defineEmits(['update:modelValue']);

const MIN_HOUR = 0;
const MAX_HOUR = 23;
const MIN_MINUTE = 0;
const MAX_MINUTE = 59;
const MINUTES_PER_HOUR = 60;
const DEFAULT_HOUR = 9;
const DEFAULT_MINUTE_STRING = '0';
const ZERO_MINUTE_STRING = '00';

const hourInput = ref<HTMLInputElement | null>(null);
const minuteInput = ref<HTMLInputElement | null>(null);

const focus = () => {
    if (hourInput.value) {
        hourInput.value.focus();
    }
};

const select = () => {
    if (hourInput.value) {
        hourInput.value.select();
    }
};

defineExpose({ focus, select });

interface Props {
    name: string;
    label?: string;
    hint?: string;
    placeholder?: string;
    isOptional?: boolean;
    isDisabled?: boolean;
    modelValue?: string;
    vuelidateField?: unknown;
    errorMessages?: string[];
    wrapperClassAddition?: string;
    customThemeBase?: FormTheme;
    customThemeNormal?: string;
    hasAutoDirty?: boolean;
    restrictMinutes?: boolean;
    stepValue?: number;
}

const props = withDefaults(defineProps<Props>(), {
    label: '',
    hint: '',
    placeholder: '',
    isOptional: false,
    isDisabled: false,
    modelValue: '09:00',
    vuelidateField: undefined,
    errorMessages: undefined,
    wrapperClassAddition: undefined,
    customThemeBase: undefined,
    customThemeNormal: undefined,
    hasAutoDirty: true,
    restrictMinutes: true,
    stepValue: 30,
});

const vuelidateErrors = computed(() => {
    if (props.vuelidateField?.$errors.length) {
        return props.vuelidateField.$errors.map((error: unknown) => error.$message);
    }

    return [];
});

const errorMessagesTotal = computed(() => {
    const vuelidateErrorsList = vuelidateErrors.value;
    const additionalErrors = props.errorMessages || [];
    return [...vuelidateErrorsList, ...additionalErrors];
});

const inputClassObject = computed(() => ({
    'text-primary focus:border-brand': !errorMessagesTotal.value.length && !props.isDisabled,
    'text-danger': errorMessagesTotal.value.length,
    'disabled:cursor-not-allowed disabled:text-muted disabled:border-secondary--inverse':
        props.isDisabled,
}));

const hourValue = computed(() => {
    if (!props.modelValue) return DEFAULT_HOUR.toString().padStart(2, DEFAULT_MINUTE_STRING);
    return props.modelValue.split(':')[0];
});

const minuteDisplayValue = computed(() => {
    if (!props.modelValue) return ZERO_MINUTE_STRING;
    return props.modelValue.split(':')[1];
});

const minuteValue = computed(() => {
    if (!props.modelValue) return ZERO_MINUTE_STRING;
    const minute = props.modelValue.split(':')[1];

    if (props.restrictMinutes) {
        const minuteNum = parseInt(minute);
        const step = props.stepValue;
        const halfStep = step / 2;

        if (minuteNum >= MIN_MINUTE && minuteNum < halfStep) return ZERO_MINUTE_STRING;
        if (minuteNum >= halfStep && minuteNum < MINUTES_PER_HOUR - halfStep)
            return step.toString().padStart(2, DEFAULT_MINUTE_STRING);
        if (minuteNum >= MINUTES_PER_HOUR - halfStep && minuteNum <= MAX_MINUTE)
            return ZERO_MINUTE_STRING;
        return ZERO_MINUTE_STRING;
    }

    return minute;
});

const adjustMinuteValue = (minute: number): string => {
    if (!props.restrictMinutes) {
        return minute.toString().padStart(2, DEFAULT_MINUTE_STRING);
    }

    const step = props.stepValue;
    const halfStep = step / 2;

    if (minute >= MIN_MINUTE && minute < halfStep) return ZERO_MINUTE_STRING;
    if (minute >= halfStep && minute < MINUTES_PER_HOUR - halfStep)
        return step.toString().padStart(2, DEFAULT_MINUTE_STRING);
    if (minute >= MINUTES_PER_HOUR - halfStep && minute <= MAX_MINUTE) return ZERO_MINUTE_STRING;

    return ZERO_MINUTE_STRING;
};

const handleHourInput = (event: Event) => {
    const inputElement = event.target as HTMLInputElement;
    const inputValue = inputElement.value;
    let hour = parseInt(inputValue);
    const originalHour = hour;

    if (isNaN(hour) || hour < MIN_HOUR) hour = MIN_HOUR;
    if (hour > MAX_HOUR) hour = MAX_HOUR;

    const minute = minuteValue.value;
    const newTime = `${hour.toString().padStart(2, DEFAULT_MINUTE_STRING)}:${minute}`;
    emit('update:modelValue', newTime);

    // If correction was needed, immediately update the input field value
    // to prevent showing invalid values (e.g., "52" instead of "23")
    if (hour !== originalHour || (isNaN(originalHour) && inputValue !== '')) {
        const correctedValue = hour.toString().padStart(2, DEFAULT_MINUTE_STRING);
        // Update the input field directly to show corrected value immediately
        inputElement.value = correctedValue;
    }
};

const handleHourBlur = () => {
    // Ensure the hour value is valid on blur
    const currentHour = parseInt(hourValue.value);
    let correctedHour = currentHour;

    if (isNaN(currentHour) || currentHour < MIN_HOUR) {
        correctedHour = MIN_HOUR;
    } else if (currentHour > MAX_HOUR) {
        correctedHour = MAX_HOUR;
    }

    // If correction is needed, update the value
    if (correctedHour !== currentHour) {
        const minute = minuteValue.value;
        const newTime = `${correctedHour.toString().padStart(2, DEFAULT_MINUTE_STRING)}:${minute}`;
        emit('update:modelValue', newTime);

        // Update the input field to reflect the corrected value
        if (hourInput.value) {
            hourInput.value.value = correctedHour.toString().padStart(2, DEFAULT_MINUTE_STRING);
        }
    }

    if (props.hasAutoDirty && props.vuelidateField) {
        props.vuelidateField.$touch();
    }
};

const handleMinuteInput = (event: Event) => {
    let minute = parseInt((event.target as HTMLInputElement).value);
    if (isNaN(minute) || minute < MIN_MINUTE) minute = MIN_MINUTE;
    if (minute > MAX_MINUTE) minute = MAX_MINUTE;

    const newTime = `${hourValue.value}:${minute.toString().padStart(2, DEFAULT_MINUTE_STRING)}`;
    emit('update:modelValue', newTime);
};

const handleMinuteBlur = () => {
    if (props.restrictMinutes) {
        const currentMinute = parseInt(minuteDisplayValue.value);
        const adjustedMinute = adjustMinuteValue(currentMinute);
        let newHour = parseInt(hourValue.value);

        if (
            currentMinute >= MINUTES_PER_HOUR - props.stepValue / 2 &&
            currentMinute <= MAX_MINUTE &&
            adjustedMinute === ZERO_MINUTE_STRING
        ) {
            newHour = newHour >= MAX_HOUR ? MIN_HOUR : newHour + 1;
        }

        const newTime = `${newHour.toString().padStart(2, DEFAULT_MINUTE_STRING)}:${adjustedMinute}`;
        emit('update:modelValue', newTime);

        if (minuteInput.value) {
            minuteInput.value.value = adjustedMinute;
        }
    }

    if (props.hasAutoDirty && props.vuelidateField) {
        props.vuelidateField.$touch();
    }
};

const incrementTime = () => {
    if (props.restrictMinutes) {
        const currentMinute = parseInt(minuteValue.value);
        const step = props.stepValue;
        const newMinute = currentMinute === MIN_MINUTE ? step : MIN_MINUTE;
        let newHour = parseInt(hourValue.value);

        if (newMinute === MIN_MINUTE && currentMinute === step) {
            newHour = newHour >= MAX_HOUR ? MIN_HOUR : newHour + 1;
        }

        const newTime = `${newHour.toString().padStart(2, DEFAULT_MINUTE_STRING)}:${newMinute.toString().padStart(2, DEFAULT_MINUTE_STRING)}`;
        emit('update:modelValue', newTime);
    } else {
        let currentMinute = parseInt(minuteValue.value);
        let newHour = parseInt(hourValue.value);

        currentMinute++;
        if (currentMinute >= MINUTES_PER_HOUR) {
            currentMinute = MIN_MINUTE;
            newHour = newHour >= MAX_HOUR ? MIN_HOUR : newHour + 1;
        }

        const newTime = `${newHour.toString().padStart(2, DEFAULT_MINUTE_STRING)}:${currentMinute.toString().padStart(2, DEFAULT_MINUTE_STRING)}`;
        emit('update:modelValue', newTime);
    }
};

const decrementTime = () => {
    if (props.restrictMinutes) {
        const currentMinute = parseInt(minuteValue.value);
        const step = props.stepValue;
        const newMinute = currentMinute === step ? MIN_MINUTE : step;
        let newHour = parseInt(hourValue.value);

        if (newMinute === step && currentMinute === MIN_MINUTE) {
            newHour = newHour <= MIN_HOUR ? MAX_HOUR : newHour - 1;
        }

        const newTime = `${newHour.toString().padStart(2, DEFAULT_MINUTE_STRING)}:${newMinute.toString().padStart(2, DEFAULT_MINUTE_STRING)}`;
        emit('update:modelValue', newTime);
    } else {
        let currentMinute = parseInt(minuteValue.value);
        let newHour = parseInt(hourValue.value);

        currentMinute--;
        if (currentMinute < MIN_MINUTE) {
            currentMinute = MAX_MINUTE;
            newHour = newHour <= MIN_HOUR ? MAX_HOUR : newHour - 1;
        }

        const newTime = `${newHour.toString().padStart(2, DEFAULT_MINUTE_STRING)}:${currentMinute.toString().padStart(2, DEFAULT_MINUTE_STRING)}`;
        emit('update:modelValue', newTime);
    }
};
</script>

<script lang="ts">
export default {
    inheritAttrs: false,
};
</script>
